import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import { fetchBlogPosts } from 'helpers/blog';

const BlogListPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchBlogPosts();
        setPosts(response.posts || []);
      } catch (requestError) {
        setError(requestError.response?.data?.error || t('BLOG.ERROR_LOAD'));
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, [t]);

  if (loading) {
    return <div className="d-flex justify-content-center py-6"><Spinner animation="border" /></div>;
  }

  return (
    <>
      <PageHeader
        title={t('BLOG.LIST_TITLE')}
        description={t('BLOG.LIST_DESCRIPTION')}
        className="mb-4"
      >
        <div className="mt-3">
          <Button as={Link} to="/workspace/blog/new">
            {t('BLOG.CREATE_NEW')}
          </Button>
        </div>
      </PageHeader>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-4">
        {posts.map(post => (
          <Col xl={4} md={6} key={post.id}>
            <Card className="h-100 border overflow-hidden">
              <div className="ratio ratio-4x3">
                {post.cover_image_url ? (
                  <img
                    src={post.cover_image_url}
                    alt={post.title}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${post.accent_color || '#0b5ed7'}, #0b1727)` }} />
                )}
              </div>
              <Card.Body>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {post.category ? <Badge bg="light" text="dark">{post.category}</Badge> : null}
                  <Badge bg={post.is_published ? 'success' : 'secondary'}>
                    {post.is_published ? t('BLOG.STATUS_PUBLISHED') : t('BLOG.STATUS_DRAFT')}
                  </Badge>
                  {post.is_featured ? <Badge bg="warning" text="dark">{t('BLOG.STATUS_FEATURED')}</Badge> : null}
                </div>
                <h5 className="mb-2">{post.title}</h5>
                {post.subtitle ? <p className="text-700 mb-2">{post.subtitle}</p> : null}
                <p className="text-600 fs--1 mb-3">{post.excerpt || t('BLOG.NO_EXCERPT')}</p>
                <div className="text-500 fs--2 mb-3">
                  {[post.author_name || post.author?.full_name, post.reading_time].filter(Boolean).join(' · ')}
                </div>
                <Button as={Link} to={`/workspace/blog/${post.id}`} variant="outline-primary" size="sm">
                  {t('BLOG.VIEW_DETAIL')}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
        {!posts.length ? (
          <Col xs={12}>
            <Alert variant="light">{t('BLOG.EMPTY')}</Alert>
          </Col>
        ) : null}
      </Row>
    </>
  );
};

export default BlogListPage;
